import { useCallback } from 'react';
import type { StripeElements } from '@stripe/stripe-js';

import type StripeApi from '../../services/stripe/stripe';
import { focusFirstStripeFormError } from '../../utils/accessibility/focusFirstStripeFormError';

type FormErrors = Partial<Record<string, string>>;

type UseStripeFormValidationParams = {
  elements: StripeElements | null;
  stripeApi: typeof StripeApi;
  selectorByErrorKey: Partial<Record<string, string>>;
  // Scopes focus lookups to the form's subtree so a duplicate
  // `#paymentElement`/field id from another mounted form (e.g. a
  // background screen behind a secondary drawer) can't be matched instead.
  root?: ParentNode;
};

/**
 * Validates the Stripe payment element alongside platform form errors and
 * moves focus to whichever invalid field comes first in the DOM.
 */
const useStripeFormValidation = ({
  elements,
  stripeApi,
  selectorByErrorKey,
  root,
}: UseStripeFormValidationParams) => {
  const validateAndFocusFirstError = useCallback(
    async (formErrors: FormErrors = {}) => {
      const hasStripeError = elements
        ? Boolean(
            (
              await stripeApi.validatePaymentMethod({
                elements,
                focusOnError: false,
                root,
              })
            ).validationError,
          )
        : true;
      const hasFormErrors = Object.keys(formErrors).length > 0;

      if (!hasStripeError && !hasFormErrors) {
        return true;
      }

      focusFirstStripeFormError({
        errors: formErrors,
        selectorByErrorKey,
        hasStripeError,
        elements,
        stripeApi,
        root,
      });

      return false;
    },
    [elements, stripeApi, selectorByErrorKey, root],
  );

  return { validateAndFocusFirstError };
};

export default useStripeFormValidation;
